import { useQuery } from "@tanstack/react-query";
import {
  fetchMarineConditions,
  fetchMarineWeather,
  fetchTides,
} from "@/lib/fishing-data";
import {
  buildAlmanac,
  compassPoint,
  formatAlmanacTime,
  skyLabel,
  type AlmanacNote,
  type AlmanacTide,
} from "@/lib/daily-almanac";
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  Clock,
  Gauge,
  Loader2,
  Sunrise,
  Sunset,
  Thermometer,
  Waves,
  Wind,
} from "lucide-react";

function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Wind;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-background/60 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        <Icon size={12} aria-hidden />
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function TideRow({ tide, isNext }: { tide: AlmanacTide; isNext: boolean }) {
  const High = tide.type === "H" ? ArrowUp : ArrowDown;
  return (
    <li
      className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
        isNext ? "border-[var(--electric)]/60 bg-[var(--electric)]/10" : "border-border bg-background/50"
      }`}
    >
      <High size={14} aria-hidden className="text-[var(--electric)]" />
      <span className="w-10 text-xs font-semibold uppercase tracking-wider">
        {tide.type === "H" ? "High" : "Low"}
      </span>
      <span className="font-semibold tabular-nums">{formatAlmanacTime(tide.timeMs)}</span>
      <span className="text-sm text-muted-foreground tabular-nums">{tide.heightFt.toFixed(1)} ft</span>
      <span className="ml-auto text-xs text-muted-foreground tabular-nums">
        window {formatAlmanacTime(tide.windowStartMs)}–{formatAlmanacTime(tide.windowEndMs)}
      </span>
    </li>
  );
}

const NOTE_TONE: Record<AlmanacNote["tone"], string> = {
  good: "border-emerald-500/50 bg-emerald-500/10",
  caution: "border-amber-500/50 bg-amber-500/10",
  info: "border-border bg-background/50",
};

/**
 * Fisherman's daily almanac — one card that answers "what's the day look like,
 * when do I fish it, and what should I keep in mind?" using only the data the
 * site already stores.
 */
export function DailyAlmanac() {
  const weather = useQuery({
    queryKey: ["marine-weather"],
    queryFn: () => fetchMarineWeather(),
    staleTime: 15 * 60_000,
  });
  const marine = useQuery({
    queryKey: ["marine-conditions"],
    queryFn: () => fetchMarineConditions(),
    staleTime: 15 * 60_000,
  });
  const tides = useQuery({
    queryKey: ["tides"],
    queryFn: () => fetchTides(),
    staleTime: 60 * 60_000,
  });

  const loading = weather.isLoading || marine.isLoading || tides.isLoading;
  const almanac = buildAlmanac({
    weather: (weather.data as any) ?? null,
    marine: (marine.data as any) ?? null,
    tides: tides.data ?? null,
  });

  return (
    <section
      aria-labelledby="daily-almanac-heading"
      className="rounded-2xl border border-border bg-card/70 p-5 shadow-sm backdrop-blur"
    >
      <header className="mb-4 flex flex-wrap items-center gap-2">
        <BookOpen size={16} aria-hidden className="text-[var(--electric)]" />
        <h2 id="daily-almanac-heading" className="text-lg font-semibold">
          Daily Almanac
        </h2>
        <span className="text-sm text-muted-foreground">{almanac.dateLabel} · Coffman Cove</span>
        {loading && <Loader2 size={14} aria-hidden className="ml-auto animate-spin text-muted-foreground" />}
      </header>

      {!loading && almanac.empty ? (
        <p className="text-sm text-muted-foreground">
          Conditions data is temporarily unavailable. Tide and weather notes return as soon as the next
          update lands.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat
              icon={Thermometer}
              label="Air"
              value={
                almanac.highF != null && almanac.lowF != null
                  ? `${Math.round(almanac.highF)}° / ${Math.round(almanac.lowF)}°`
                  : "—"
              }
              hint={skyLabel(almanac.skyCode)}
            />
            <Stat
              icon={Wind}
              label="Wind"
              value={almanac.windMaxKn != null ? `${Math.round(almanac.windMaxKn)} kn` : "—"}
              hint={`${compassPoint(almanac.windDirDeg)} · max today`}
            />
            <Stat
              icon={Waves}
              label="Seas"
              value={almanac.seasFt != null ? `${almanac.seasFt.toFixed(1)} ft` : "—"}
              hint={almanac.waterTempF != null ? `${Math.round(almanac.waterTempF)}°F water` : undefined}
            />
            <Stat
              icon={Gauge}
              label="Barometer"
              value={almanac.pressureMb != null ? `${Math.round(almanac.pressureMb)} mb` : "—"}
              hint={`${almanac.pressureTrend}${
                almanac.pressureDelta ? ` ${almanac.pressureDelta > 0 ? "+" : ""}${almanac.pressureDelta.toFixed(1)}` : ""
              }`}
            />
            <Stat
              icon={Sunrise}
              label="Sunrise"
              value={almanac.sunriseMs != null ? formatAlmanacTime(almanac.sunriseMs) : "—"}
              hint={almanac.daylightLabel ? `${almanac.daylightLabel} daylight` : undefined}
            />
            <Stat
              icon={Sunset}
              label="Sunset"
              value={almanac.sunsetMs != null ? formatAlmanacTime(almanac.sunsetMs) : "—"}
              hint={
                almanac.precipChancePct != null ? `${Math.round(almanac.precipChancePct)}% rain chance` : undefined
              }
            />
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                <Clock size={12} aria-hidden /> Tide windows today
              </h3>
              {almanac.tides.length ? (
                <ul className="space-y-2">
                  {almanac.tides.map((t) => (
                    <TideRow key={t.timeMs} tide={t} isNext={almanac.nextTide?.timeMs === t.timeMs} />
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Tide predictions are refreshing.</p>
              )}
            </div>

            <div>
              <h3 className="mb-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Notes for the day
              </h3>
              <ul className="space-y-2">
                {almanac.notes.map((n) => (
                  <li key={n.id} className={`rounded-lg border px-3 py-2 text-sm ${NOTE_TONE[n.tone]}`}>
                    {n.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
